// Order mentions — the `@<orderNumber>` tokens staff drop into group messages.
//
// Design: the token lives INLINE in the message body so the text still reads
// correctly anywhere that doesn't understand mentions (the rooms-list preview,
// the mobile RoomChatModal, a copied message). The structured `mentions` array
// on the message doc is the resolution layer — it maps a token back to a real
// `orderId`.
//
// Why both: `orders` in AppContext is dispatch-scoped (services/orderScope.ts).
// Dispatch A can mention an order Dispatch B has never downloaded, so resolving
// by orderNumber alone would dead-end for B. The stored id lets B fetch the doc
// directly (firestore.rules allows any authed non-manager to read orders).

import { Order, OrderMention, RoomMention, User } from '../types';

// Order numbers are `YYYYMMDD-NNN` (OrderManagement.generateOrderNumber), but
// legacy/AI orders vary, so accept any run of word chars and dashes. The
// leading boundary check keeps `you@example.com` from matching.
const TOKEN = /(^|[\s(\[{,;:])@([A-Za-z0-9][A-Za-z0-9-]*)/g;

/** Display label for an order — order number, else a short id (the same
 *  fallback OrderManagement uses for its `#` chips). */
export const orderLabel = (order: Pick<Order, 'id' | 'orderNumber'>): string =>
  order.orderNumber || order.id.slice(-6).toUpperCase();

/** The text inserted into the composer when a picker row is chosen. */
export const mentionToken = (order: Pick<Order, 'id' | 'orderNumber'>): string =>
  `@${orderLabel(order)}`;

export interface MentionSegment {
  type: 'text' | 'mention';
  /** Raw text for `text`; the token WITHOUT the `@` for `mention`. */
  value: string;
  /** Only on `mention`, and only when the token resolved to an order. */
  orderId?: string;
}

/**
 * Split a message body into plain-text and mention segments.
 *
 * A token resolves when either the message's stored `mentions` array names it
 * (authoritative) or a visible order carries that order number (so numbers
 * typed by hand still linkify). Unresolvable tokens stay plain text rather than
 * rendering a chip that goes nowhere on click.
 */
export function parseMentions(
  body: string,
  mentions: OrderMention[] | undefined,
  orders: Pick<Order, 'id' | 'orderNumber'>[],
): MentionSegment[] {
  if (!body) return [];

  const byNumber = new Map<string, string>();
  // Visible orders first, so an explicit stored mention wins on collision.
  for (const o of orders) {
    if (o.orderNumber) byNumber.set(o.orderNumber.toLowerCase(), o.id);
  }
  for (const m of mentions ?? []) byNumber.set(m.orderNumber.toLowerCase(), m.orderId);

  const segments: MentionSegment[] = [];
  let last = 0;
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = TOKEN.exec(body)) !== null) {
    const [full, prefix, token] = match;
    const orderId = byNumber.get(token.toLowerCase());
    if (!orderId) continue; // leave unknown @words as plain text

    const start = match.index + prefix.length;
    if (start > last) segments.push({ type: 'text', value: body.slice(last, start) });
    segments.push({ type: 'mention', value: token, orderId });
    last = match.index + full.length;
  }

  if (last < body.length) segments.push({ type: 'text', value: body.slice(last) });
  return segments;
}

/**
 * Drop mentions whose token no longer appears in the body — the user typed the
 * mention, then backspaced over it before sending (or while editing). Without
 * this the doc keeps a chip the text can't show.
 */
export function pruneMentions(body: string, mentions: OrderMention[]): OrderMention[] {
  const present = new Set<string>();
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(body)) !== null) present.add(match[2].toLowerCase());

  const seen = new Set<string>();
  return mentions.filter((m) => {
    const key = m.orderNumber.toLowerCase();
    if (!present.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Read the in-progress `@` query at the caret, or null when the caret isn't in
 * one. Returns the query text plus the token's start offset so the caller can
 * splice a replacement in.
 */
export function activeMentionQuery(
  text: string,
  caret: number,
): { query: string; start: number } | null {
  const upToCaret = text.slice(0, caret);
  const at = upToCaret.lastIndexOf('@');
  if (at === -1) return null;

  // Must start a word — `@` glued to the end of a word is an email, not a mention.
  const before = at > 0 ? upToCaret[at - 1] : '';
  if (before && !/[\s(\[{,;:]/.test(before)) return null;

  const query = upToCaret.slice(at + 1);
  // Whitespace closes the token; order numbers never contain spaces.
  if (/\s/.test(query)) return null;
  return { query, start: at };
}

/** Rank orders for the picker: order-number matches before customer-name
 *  matches, newest first within each group. */
export function searchOrdersForMention(
  orders: Order[],
  query: string,
  limit = 8,
): Order[] {
  const q = query.trim().toLowerCase();
  const scored: Array<{ order: Order; rank: number }> = [];

  for (const o of orders) {
    const label = orderLabel(o).toLowerCase();
    const name = (o.customerName || '').toLowerCase();
    if (!q) {
      scored.push({ order: o, rank: 2 });
    } else if (label.startsWith(q)) {
      scored.push({ order: o, rank: 0 });
    } else if (label.includes(q)) {
      scored.push({ order: o, rank: 1 });
    } else if (name.includes(q)) {
      scored.push({ order: o, rank: 2 });
    }
  }

  // Two sources share this list: delivery orders and Order Board cards (ids
  // `card_…`, services/boardMentions). Ranked together by recency the board
  // won every slot on a bare `@` — a day's website orders are all newer than
  // the delivery run — so dispatch saw only board orders until they typed a
  // number. Within each match quality the two sources are now DEALT
  // ALTERNATELY (delivery, board, delivery, …), each newest first.
  //
  // The order must not depend on `limit`: the picker grows the limit as the
  // reader scrolls, and rows have to APPEND — a list that reshuffled under the
  // pointer on every page would be unusable. Slicing one fixed ordering gives
  // exactly that, and either source simply runs on when the other is spent.
  const newest = (a: { order: Order }, b: { order: Order }) => b.order.createdAt - a.order.createdAt;
  const isBoard = (o: Order) => o.id.startsWith('card_');
  const out: Order[] = [];
  for (const rank of [0, 1, 2]) {
    const d = scored.filter((s) => s.rank === rank && !isBoard(s.order)).sort(newest);
    const c = scored.filter((s) => s.rank === rank && isBoard(s.order)).sort(newest);
    for (let i = 0; i < Math.max(d.length, c.length); i++) {
      if (i < d.length) out.push(d[i].order);
      if (i < c.length) out.push(c[i].order);
    }
    if (out.length >= limit) break;
  }
  return out.slice(0, limit);
}

// ── Parity: @user / @all alongside @order (FEATURES.chatParity) ─────────────
//
// The body stays PLAIN TEXT — `@Sara`, `@all`, `@20260914-001` — for the same
// reason the order grammar does: the rooms-list preview, a copied message, a
// push notification body and a pre-parity client all have to read correctly
// without understanding mentions. The structured rows on the message are the
// resolution layer, exactly as `mentions` is for orders.
//
// A person's token is the FIRST WORD of their name, sanitised to the same
// character class the order token uses, because whitespace closes a token (an
// `@Sara Khan` that linkified across the space would swallow the next word).
// Two people called Sara therefore share a token; the stored `userId` on the
// mention row is what the chip actually resolves through, and the picker shows
// full names, so the ambiguity is cosmetic — the same trade WhatsApp makes.

/** The literal `@all` token. Every member may use it (Umer, 2026-09-14). */
export const ALL_TOKEN = 'all';

/** First word of a name, reduced to the token character class. '' when nothing
 *  usable is left (a name that is only punctuation or emoji). */
export const userTokenName = (name: string | undefined): string =>
  (name ?? '').trim().split(/\s+/)[0]?.replace(/[^A-Za-z0-9-]/g, '') ?? '';

/** The text inserted into the composer when a person row is chosen. */
export const userMentionToken = (user: Pick<User, 'name'>): string => `@${userTokenName(user.name)}`;

export type MentionCandidate =
  | { kind: 'all'; key: 'all'; token: string }
  | { kind: 'user'; key: string; token: string; user: User }
  | { kind: 'order'; key: string; token: string; order: Order };

/**
 * Rank everything `@` can mean for one query, in the order the picker shows it:
 * Everyone, then people, then orders. Each section is capped separately so a
 * busy day's orders cannot push the people off the list (the complaint that
 * started this: dispatch could not @ a driver because 8 orders matched first).
 */
export function searchMentionTargets(
  query: string,
  source: { users: User[]; orders: Order[] },
  limits: { users?: number; orders?: number } = {},
): MentionCandidate[] {
  const q = query.trim().toLowerCase();
  const out: MentionCandidate[] = [];

  if (!q || ALL_TOKEN.startsWith(q)) out.push({ kind: 'all', key: 'all', token: `@${ALL_TOKEN}` });

  const people = source.users
    .filter((u) => !u.isArchived && userTokenName(u.name))
    .map((u) => {
      const name = (u.name ?? '').toLowerCase();
      const token = userTokenName(u.name).toLowerCase();
      if (!q) return { u, rank: 2 };
      if (token.startsWith(q)) return { u, rank: 0 };
      if (name.startsWith(q)) return { u, rank: 1 };
      if (name.includes(q)) return { u, rank: 2 };
      return null;
    })
    .filter((x): x is { u: User; rank: number } => !!x)
    .sort((a, b) => a.rank - b.rank || (a.u.name ?? '').localeCompare(b.u.name ?? ''))
    .slice(0, limits.users ?? 6);
  for (const { u } of people) out.push({ kind: 'user', key: `u:${u.id}`, token: userMentionToken(u), user: u });

  for (const o of searchOrdersForMention(source.orders, query, limits.orders ?? 6)) {
    out.push({ kind: 'order', key: `o:${o.id}`, token: mentionToken(o), order: o });
  }
  return out;
}

export type ParsedSegment =
  | { type: 'text'; value: string }
  | { type: 'order'; value: string; orderId: string }
  | { type: 'user'; value: string; userId?: string }
  | { type: 'all'; value: string };

/**
 * Split a body into text and mention segments, resolving all three kinds.
 *
 * `@all` always renders as a chip — it names no one, so it cannot dead-end.
 * `@Name` and `@order` render as chips ONLY when they resolve, for the reason
 * the order grammar already gives: a chip that goes nowhere on click is worse
 * than plain text. An `@Name` with no matching mention row is therefore left
 * alone, which is also what happens to `you@example.com` and `@here`.
 */
export function parseAllMentions(
  body: string,
  opts: {
    orderMentions?: OrderMention[];
    userMentions?: RoomMention[];
    orders?: Pick<Order, 'id' | 'orderNumber'>[];
  },
): ParsedSegment[] {
  if (!body) return [];

  const orderByNumber = new Map<string, string>();
  for (const o of opts.orders ?? []) {
    if (o.orderNumber) orderByNumber.set(o.orderNumber.toLowerCase(), o.id);
  }
  for (const m of opts.orderMentions ?? []) orderByNumber.set(m.orderNumber.toLowerCase(), m.orderId);

  const userByToken = new Map<string, string | undefined>();
  let mentionsAll = false;
  for (const m of opts.userMentions ?? []) {
    if (m.kind === 'all') { mentionsAll = true; continue; }
    if (m.kind === 'user') {
      const label = (m.label || '').toLowerCase() || userTokenName(m.label).toLowerCase();
      if (label) userByToken.set(label, m.userId);
    }
    if (m.kind === 'order' && m.orderNumber && m.orderId) {
      orderByNumber.set(m.orderNumber.toLowerCase(), m.orderId);
    }
  }

  const segments: ParsedSegment[] = [];
  let last = 0;
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(body)) !== null) {
    const [full, prefix, token] = match;
    const lower = token.toLowerCase();
    let seg: ParsedSegment | null = null;
    if (lower === ALL_TOKEN && mentionsAll) seg = { type: 'all', value: token };
    else if (orderByNumber.has(lower)) seg = { type: 'order', value: token, orderId: orderByNumber.get(lower)! };
    else if (userByToken.has(lower)) seg = { type: 'user', value: token, userId: userByToken.get(lower) };
    if (!seg) continue;

    const start = match.index + prefix.length;
    if (start > last) segments.push({ type: 'text', value: body.slice(last, start) });
    segments.push(seg);
    last = match.index + full.length;
  }
  if (last < body.length) segments.push({ type: 'text', value: body.slice(last) });
  return segments;
}

/** Drop mention rows whose token no longer appears in the body — the user typed
 *  one and backspaced over it. Same job as `pruneMentions` for the order-only
 *  array, over the three-kind rows. */
export function pruneRoomMentions(body: string, mentions: RoomMention[]): RoomMention[] {
  const present = new Set<string>();
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(body)) !== null) present.add(match[2].toLowerCase());

  const seen = new Set<string>();
  return mentions.filter((m) => {
    const key =
      m.kind === 'all' ? ALL_TOKEN
        : m.kind === 'order' ? (m.orderNumber ?? '').toLowerCase()
          : (m.label ?? '').toLowerCase();
    const dedupe = m.kind === 'user' ? `u:${m.userId ?? key}` : m.kind === 'order' ? `o:${m.orderId ?? key}` : 'all';
    if (!key || !present.has(key) || seen.has(dedupe)) return false;
    seen.add(dedupe);
    return true;
  });
}
